import type { Claim, ClaimStatusTemplate } from "@/types/claims";
import type { ClaimFolder } from "@/api/generated/client/models/ClaimFolder";
import type { ClaimPipeline } from "@/api/generated/client/models/ClaimPipeline";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Archive, Calendar, Hash, MapPin, Trash2, Undo2, User } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { FolderPicker } from "@/components/claims/folders/FolderPicker";
import { ClaimAssigneePicker } from "@/components/claims/ClaimAssigneePicker";
import { ClaimRowStatusSelect } from "@/components/claims/ClaimRowStatusSelect";
import { motion, AnimatePresence } from "framer-motion";
import { formatDate } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

import { useLocation } from "wouter";

interface ClaimsListProps {
    claims: Claim[];
    statuses: Pick<ClaimStatusTemplate, "id" | "pipelineId" | "displayName" | "color">[];
    pipelines?: ClaimPipeline[];
    assignablePipelines?: ClaimPipeline[];
    viewerCompanyId?: number | null;
    /** Full accessible folder list — used to render a claim's current folder label. */
    folders: ClaimFolder[];
    /** Subset the user can assign into; defaults to `folders`. */
    assignableFolders?: ClaimFolder[];
    onStatusChange: (id: number, statusTemplateId: number) => void;
    onPipelineChange?: (id: number, pipelineId: number) => void;
    onFolderChange: (id: number, folderId: number | null) => void;
    onArchiveToggle: (id: number, isArchived: boolean) => void;
    onDeleteClick: (id: number) => void;
    isArchiving?: boolean;
    isLoading: boolean;
}

export function ClaimsList({
    claims,
    statuses,
    pipelines = [],
    assignablePipelines = [],
    viewerCompanyId = null,
    folders,
    assignableFolders,
    onStatusChange,
    onPipelineChange = () => undefined,
    onFolderChange,
    onArchiveToggle,
    onDeleteClick,
    isArchiving = false,
    isLoading,
}: ClaimsListProps) {
    const [, setLocation] = useLocation();
    // Desktop (>= md, 768px) renders the original card layout untouched; the
    // redesigned card is scoped to mobile only. Branching on a media query keeps
    // exactly one card mounted per breakpoint so each can use its own entrance
    // animation without the other's transform interfering with dropdown anchoring.
    const isDesktop = useMediaQuery("(min-width: 768px)");

    if (isLoading) {
        return <div className="space-y-4">
            {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse" />
            ))}
        </div>;
    }

    if (claims.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                No claims found matching your criteria.
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={300}>
            <div className="space-y-4">
                <AnimatePresence>
                    {claims.map((claim) => {
                        const currentPipeline = pipelines.find((pipeline) => pipeline.id === claim.pipelineId);
                        const pipelineOptions = claim.companyId === viewerCompanyId ? assignablePipelines : [];
                        const canChangePipeline = (claim.canWrite ?? false) && pipelineOptions.length > 0;
                        const pipelineName = currentPipeline?.name ?? claim.claimPipelineName ?? null;
                        const pipelineColor = currentPipeline?.color ?? claim.claimPipelineColor ?? "#94A3B8";
                        const currentStatus = statuses.find((status) => status.id === claim.claimStatusTemplateId && status.pipelineId === claim.pipelineId);
                        const statusColor = currentStatus?.color ?? claim.claimStatusColor ?? "#94A3B8";

                        // ---- Desktop (>= md): original layout, unchanged from main ----
                        if (isDesktop) {
                            return (
                                <motion.div
                                    key={claim.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Card
                                        className="relative overflow-hidden border-border/60 transition-all hover:border-border hover:shadow-md cursor-pointer hover:bg-muted/50"
                                        onClick={() => setLocation(`/claims/${claim.id}`)}
                                    >
                                        {/* Status color accent */}
                                        <span
                                            className="absolute inset-y-0 left-0 w-1.5"
                                            style={{ backgroundColor: statusColor }}
                                            aria-hidden="true"
                                        />
                                        <CardContent className="p-4 pl-5">
                                            <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center text-sm text-muted-foreground">
                                                            <User className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                                            <span className="truncate">{claim.insuredName}</span>
                                                        </div>
                                                        <div className="flex items-center text-sm text-muted-foreground">
                                                            <MapPin className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                                            <span className="truncate">{claim.propertyAddress || 'No address'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Hash className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                            <h3 className="text-sm text-muted-foreground truncate">
                                                                {claim.claimNumber}
                                                            </h3>
                                                            {!(claim.canWrite ?? false) && (
                                                                <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-amber-700 border-amber-300 bg-amber-50">
                                                                    View only
                                                                </Badge>
                                                            )}
                                                            {claim.isArchived && (
                                                                <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                                                                    Archived
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                                            <span className="flex items-center" title="Date of Loss">
                                                                <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                                                <span className="text-xs">DOL:</span>&nbsp;{formatDate(claim.dateOfLoss)}
                                                            </span>
                                                            <span className="flex items-center" title="Date Created">
                                                                <span className="text-xs">Created:</span>&nbsp;{formatDate(claim.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col items-end gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-1.5">
                                                            {claim.pipelineId && (
                                                                canChangePipeline ? (
                                                                <Select
                                                                    value={claim.pipelineId?.toString()}
                                                                    onValueChange={(pipelineId) => onPipelineChange(claim.id, parseInt(pipelineId))}
                                                                >
                                                                    <SelectTrigger className="w-[140px] h-8 text-xs">
                                                                        <SelectValue>
                                                                            {pipelineName ? (
                                                                                <span className="inline-flex items-center gap-1.5">
                                                                                    <span
                                                                                        className="h-2.5 w-2.5 rounded-full border"
                                                                                        style={{ backgroundColor: pipelineColor }}
                                                                                    />
                                                                                    <span className="truncate">{pipelineName}</span>
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted-foreground">No Workflow</span>
                                                                            )}
                                                                        </SelectValue>
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {pipelineOptions.map((pipeline) => (
                                                                            <SelectItem key={pipeline.id} value={pipeline.id.toString()}>
                                                                                <span className="inline-flex items-center gap-2">
                                                                                    <span
                                                                                        className="h-2.5 w-2.5 rounded-full border"
                                                                                        style={{ backgroundColor: pipeline.color ?? "#94A3B8" }}
                                                                                    />
                                                                                    {pipeline.name}
                                                                                </span>
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                ) : (
                                                                    <Badge variant="outline" className="h-8 w-[140px] min-w-0 justify-start truncate rounded-md px-2 py-0 text-xs font-medium" style={{ borderColor: pipelineColor }} title={pipelineName ?? "No Workflow"}>
                                                                        {pipelineName ?? "No Workflow"}
                                                                    </Badge>
                                                                )
                                                            )}
                                                        <ClaimRowStatusSelect
                                                            claim={claim}
                                                            statuses={statuses}
                                                            viewerCompanyId={viewerCompanyId}
                                                            onChange={(statusTemplateId) => onStatusChange(claim.id, statusTemplateId)}
                                                            className="w-[140px] h-8 text-xs"
                                                        />

                                                    </div>

                                                    {folders.length > 0 && (
                                                        <div className="flex items-center gap-2">
                                                            <FolderPicker
                                                                folders={folders}
                                                                assignableFolders={assignableFolders ?? folders}
                                                                value={claim.claimFolderId}
                                                                disabled={!(claim.canWrite ?? false)}
                                                                onChange={(folderId) => onFolderChange(claim.id, folderId)}
                                                            />
                                                            <ClaimAssigneePicker claim={claim} />
                                                            {(claim.canWrite ?? false) && (
                                                                <div className="flex items-center gap-1 border-l border-border/60 pl-1">
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-8 w-8"
                                                                                onClick={() => onArchiveToggle(claim.id, !claim.isArchived)}
                                                                                disabled={isArchiving}
                                                                                aria-label={claim.isArchived ? `Unarchive claim ${claim.claimNumber}` : `Archive claim ${claim.claimNumber}`}
                                                                            >
                                                                                {claim.isArchived ? <Undo2 className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            {claim.isArchived ? "Unarchive" : "Archive"}
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                                                                onClick={() => onDeleteClick(claim.id)}
                                                                                aria-label={`Delete claim ${claim.claimNumber}`}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>Delete</TooltipContent>
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {folders.length === 0 && (
                                                        <div className="flex items-center gap-1">
                                                            <ClaimAssigneePicker claim={claim} />
                                                            {(claim.canWrite ?? false) && (
                                                                <div className="flex items-center gap-1 border-l border-border/60 pl-1">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8"
                                                                        onClick={() => onArchiveToggle(claim.id, !claim.isArchived)}
                                                                        disabled={isArchiving}
                                                                        aria-label={claim.isArchived ? `Unarchive claim ${claim.claimNumber}` : `Archive claim ${claim.claimNumber}`}
                                                                    >
                                                                        {claim.isArchived ? <Undo2 className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {claim.isArchived ? "Unarchive" : "Archive"}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                                                        onClick={() => onDeleteClick(claim.id)}
                                                                        aria-label={`Delete claim ${claim.claimNumber}`}
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Delete</TooltipContent>
                                                            </Tooltip>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        }

                        // ---- Mobile (< md): redesigned card ----
                        return (
                    <motion.div
                        key={claim.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Card
                            className="group relative overflow-hidden border-border/60 transition-all hover:border-border hover:shadow-md cursor-pointer"
                            onClick={() => setLocation(`/claims/${claim.id}`)}
                        >
                            {/* Status color accent */}
                            <span
                                className="absolute inset-y-0 left-0 w-1.5"
                                style={{ backgroundColor: statusColor }}
                                aria-hidden="true"
                            />
                            <CardContent className="p-4 pl-5">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-col gap-1 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
                                            <span className="inline-flex shrink-0 items-center gap-1.5">
                                                <User className="h-3.5 w-3.5 text-muted-foreground" />
                                                {claim.insuredName}
                                            </span>
                                            <span className="inline-flex min-w-0 items-center gap-1.5">
                                                <MapPin className="h-3.5 w-3.5 shrink-0" />
                                                <span className="truncate">{claim.propertyAddress || 'No address'}</span>
                                            </span>
                                            <span className="inline-flex shrink-0 items-center gap-1.5">
                                                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                                                <span>{claim.claimNumber}</span>
                                                {!(claim.canWrite ?? false) && (
                                                    <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                                                        View only
                                                    </Badge>
                                                )}
                                                {claim.isArchived && (
                                                    <Badge variant="secondary" className="h-5 shrink-0 px-1.5 text-[10px]">
                                                        Archived
                                                    </Badge>
                                                )}
                                            </span>
                                            <span className="inline-flex shrink-0 items-center gap-1" title="Date of Loss">
                                                <Calendar className="h-3.5 w-3.5" />
                                                <span className="text-xs">DOL:</span> {formatDate(claim.dateOfLoss)}
                                            </span>
                                            <span className="inline-flex shrink-0 items-center gap-1" title="Date Created">
                                                <span className="text-xs">Created:</span> {formatDate(claim.createdAt)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-stretch gap-2.5 border-t border-border pt-3 sm:ml-4 sm:w-auto sm:items-end sm:border-0 sm:pt-0" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex w-full items-center gap-2">
                                            {claim.pipelineId && (
                                                canChangePipeline ? (
                                                <Select
                                                    value={claim.pipelineId?.toString()}
                                                    onValueChange={(pipelineId) => onPipelineChange(claim.id, parseInt(pipelineId))}
                                                >
                                                    <SelectTrigger className="h-8 min-w-0 flex-1 text-xs sm:w-[140px] sm:flex-none">
                                                        <SelectValue>
                                                            {pipelineName ? (
                                                                <span className="inline-flex min-w-0 items-center gap-1.5">
                                                                    <span
                                                                        className="h-2.5 w-2.5 shrink-0 rounded-full border"
                                                                        style={{ backgroundColor: pipelineColor }}
                                                                    />
                                                                    <span className="truncate">{pipelineName}</span>
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted-foreground">No Workflow</span>
                                                            )}
                                                        </SelectValue>
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {pipelineOptions.map((pipeline) => (
                                                            <SelectItem key={pipeline.id} value={pipeline.id.toString()}>
                                                                <span className="inline-flex items-center gap-2">
                                                                    <span
                                                                        className="h-2.5 w-2.5 rounded-full border"
                                                                        style={{ backgroundColor: pipeline.color ?? "#94A3B8" }}
                                                                    />
                                                                    {pipeline.name}
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                ) : (
                                                    <Badge variant="outline" className="h-8 min-w-0 flex-1 justify-start truncate rounded-md px-2 py-0 text-xs font-medium sm:w-[140px] sm:flex-none" style={{ borderColor: pipelineColor }} title={pipelineName ?? "No Workflow"}>
                                                        {pipelineName ?? "No Workflow"}
                                                    </Badge>
                                                )
                                            )}
                                            <ClaimRowStatusSelect
                                                claim={claim}
                                                statuses={statuses}
                                                viewerCompanyId={viewerCompanyId}
                                                onChange={(statusTemplateId) => onStatusChange(claim.id, statusTemplateId)}
                                                className="h-8 min-w-0 flex-1 text-xs sm:w-[140px] sm:flex-none"
                                            />
                                        </div>

                                        <div className="flex items-center justify-between gap-2 sm:justify-end">
                                            {/* Assignment controls — folder & assignee */}
                                            <div className="flex min-w-0 items-center gap-0.5">
                                                {folders.length > 0 && (
                                                    <FolderPicker
                                                        folders={folders}
                                                        assignableFolders={assignableFolders ?? folders}
                                                        value={claim.claimFolderId}
                                                        disabled={!(claim.canWrite ?? false)}
                                                        onChange={(folderId) => onFolderChange(claim.id, folderId)}
                                                    />
                                                )}
                                                <ClaimAssigneePicker claim={claim} />
                                                {(claim.canWrite ?? false) && (
                                                    <div className="flex shrink-0 items-center gap-0.5 border-l border-border/60 pl-1">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8"
                                                                    onClick={() => onArchiveToggle(claim.id, !claim.isArchived)}
                                                                    disabled={isArchiving}
                                                                    aria-label={claim.isArchived ? `Unarchive claim ${claim.claimNumber}` : `Archive claim ${claim.claimNumber}`}
                                                                >
                                                                    {claim.isArchived ? <Undo2 className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                {claim.isArchived ? "Unarchive" : "Archive"}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                                                    onClick={() => onDeleteClick(claim.id)}
                                                                    aria-label={`Delete claim ${claim.claimNumber}`}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Delete</TooltipContent>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </TooltipProvider>
    );
}
